import { BookOpen, ChevronDown } from 'lucide-react';

import { Link } from '@/components/design-system';
import {
  FONT_WEIGHTS,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SIZE_SCALE,
  TYPE_SCALE,
  COLOR_SCALE,
  PADDING_TOP,
  MARGIN_TOP,
  MARGIN_BOTTOM,
  TEXT_ALIGNMENT,
} from '@/components/design-system/tokens';
import { createPageUrl } from '@/utils';

interface ResourcesSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  scrollToTop: () => void;
  onClose: () => void;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

export const ResourcesSection = ({
  isOpen,
  onToggle,
  scrollToTop,
  onClose,
  t,
}: ResourcesSectionProps) => (
  <div className={`${PADDING_TOP['4']} ${MARGIN_TOP['4']}`}>
    <div className={`${PADDING_X['3']} ${MARGIN_BOTTOM['3']}`}>
      <p
        className={`text-xs ${FONT_WEIGHTS.semibold} ${COLOR_SCALE['icon-muted']} uppercase tracking-wider`}
      >
        {t('navigation', 'menu_resources', 'Resources')}
      </p>
    </div>

    <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
      >
        <div className={`flex items-center ${GAP_SCALE['3']}`}>
          <div
            className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['blue-100-bg']} flex items-center justify-center`}
          >
            <BookOpen className={`${SIZE_SCALE.iconSm} ${COLOR_SCALE['blue-600']}`} />
          </div>
          <div className={`${TEXT_ALIGNMENT.left}`}>
            <p className={`text-sm ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
              {t('mobile_menu', 'learn_explore_title', 'Learn & Explore')}
            </p>
            <p
              className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
            >
              {t('mobile_menu', 'learn_explore_subtitle', 'About, blog & guides')}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`${SIZE_SCALE.iconMd} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`border-t ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['surface-muted']} ${PADDING_X['2']} ${PADDING_Y['2']}`}
        >
          <Link
            to={createPageUrl('AboutUs')}
            onClick={() => {
              scrollToTop();
              onClose();
            }}
            className={`block ${PADDING_X['3']} ${PADDING_Y['2.5']} text-sm font-medium ${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'about_us', 'About Us')}
          </Link>
          <Link
            to={createPageUrl('Blog')}
            onClick={() => {
              scrollToTop();
              onClose();
            }}
            className={`block ${PADDING_X['3']} ${PADDING_Y['2.5']} text-sm font-medium ${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'blog', 'Blog')}
          </Link>
          <Link
            to={createPageUrl('HowItWorks')}
            onClick={() => {
              scrollToTop();
              onClose();
            }}
            className={`block ${PADDING_X['3']} ${PADDING_Y['2.5']} text-sm font-medium ${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'how_it_works', 'How It Works')}
          </Link>
          <Link
            to={createPageUrl('Glossary')}
            onClick={() => {
              scrollToTop();
              onClose();
            }}
            className={`block ${PADDING_X['3']} ${PADDING_Y['2.5']} text-sm font-medium ${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'glossary', 'Glossary')}
          </Link>
        </div>
      )}
    </div>
  </div>
);
